import { useEffect, useMemo, useState } from 'react';
import { pct1, roleDef } from '@piaip/shared';
import { sx, sxc } from './sx';
import { useApp } from './state';
import { SignIn } from './views/SignIn';
import { Dashboard } from './views/Dashboard';
import { Overview } from './views/Overview';
import { Account } from './views/Account';
import { Entry } from './views/Entry';
import { Approvals } from './views/Approvals';
import { Payments } from './views/Payments';
import { Issues } from './views/Issues';
import { Packages } from './views/Packages';
import { Reports } from './views/Reports';
import { DataMethod } from './views/DataMethod';

export type ViewKey =
  | 'overview'
  | 'dashboard'
  | 'entry'
  | 'approvals'
  | 'payments'
  | 'issues'
  | 'packages'
  | 'reports'
  | 'data'
  | 'account';

export default function App() {
  const app = useApp();
  const { ready, me, orgName, engine } = app;
  const [view, setView] = useState<ViewKey>('dashboard');
  const [focusWeek, setFocusWeek] = useState<string | null>(null);

  // The landing tab depends on what the account is for: an executive opens on
  // the project overview, an entry-only account straight on the proforma,
  // everyone else on the dashboard.
  useEffect(() => {
    if (!me) return;
    setView(
      me.role === 'executive'
        ? 'overview'
        : app.can('entry') && !app.can('approve')
          ? 'entry'
          : 'dashboard',
    );
  }, [me?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const pending = useMemo(
    () => engine.sortedWeeks().filter((w) => w.status === 'submitted'),
    [engine],
  );

  if (!ready) {
    return (
      <div
        style={sx(
          'min-height:100vh;display:flex;align-items:center;justify-content:center;color:#5a6b68;font-size:14px',
        )}
      >
        Loading the scheme…
      </div>
    );
  }

  if (!me) {
    return (
      <div style={sx('min-height:100vh;display:flex;flex-direction:column')}>
        <SignIn />
        <Toast />
      </div>
    );
  }

  // A senior executive gets the overview and the reports only — the working
  // screens (entry, approvals, registers) never appear for them.
  const exec = me.role === 'executive';
  const navDef: [ViewKey, string, string, boolean][] = exec
    ? [
        ['overview', 'Project Overview', '', true],
        ['reports', 'Reports', '', true],
        ['account', 'My Account', '', true],
      ]
    : [
        ['dashboard', 'Dashboard', '', true],
        ['entry', 'Weekly Entry', '', app.can('entry')],
        ['approvals', 'Approvals', pending.length ? String(pending.length) : '', true],
        ['payments', 'Contractor Payments', '', app.can('payments')],
        ['issues', 'Issues', '', true],
        ['packages', 'Packages', '', true],
        ['reports', 'Reports', '', true],
        ['data', 'Data & Method', '', true],
      ];
  const navItems = navDef.filter((x) => x[3]);

  const openWeek = (id: string) => {
    setFocusWeek(id);
    setView('entry');
  };

  return (
    <div style={sx('min-height:100vh;display:flex;flex-direction:column')}>
      <div style={sx('display:flex;flex-direction:column;flex:1')}>
        <header
          data-chrome="1"
          style={sx(
            'background:linear-gradient(180deg,#14564d,#0b332d);color:#e8f0ee;padding:13px 18px;display:flex;align-items:center;gap:16px;flex-wrap:wrap;border-bottom:3px solid #c9a227;box-shadow:0 10px 24px -14px rgba(0,0,0,.7)',
          )}
        >
          <div style={sx('display:flex;flex-direction:column;gap:2px;min-width:200px;flex:1')}>
            <div
              style={sx(
                "font-family:'IBM Plex Serif',Georgia,serif;font-size:16px;font-weight:600;line-height:1.25",
              )}
            >
              Mitigation of Waterlogging in District Rahim Yar Khan
            </div>
            <div
              style={sx('font-size:10.5px;letter-spacing:.1em;text-transform:uppercase;color:#8fb3ac')}
            >
              {orgName}
            </div>
          </div>
          <div style={sx('display:flex;align-items:center;gap:14px')}>
            <div style={sx('display:flex;flex-direction:column;align-items:flex-end;gap:1px')}>
              <div style={sx('font-size:11px;color:#8fb3ac')}>Approved progress</div>
              <div
                style={sx(
                  "font-family:'IBM Plex Mono',monospace;font-size:20px;font-weight:500;color:#fff",
                )}
              >
                {pct1(engine.catPcts().overall)}%
              </div>
            </div>
            <div style={sx('width:1px;align-self:stretch;background:#2a5b54')} />
            <div
              style={sx(
                'display:flex;flex-direction:column;align-items:flex-end;gap:1px;white-space:nowrap',
              )}
            >
              <div style={sx('font-size:12px;color:#fff;font-weight:600')}>{me.name}</div>
              <div style={sx('font-size:10.5px;color:#8fb3ac')}>{roleDef(me.role).label}</div>
              <button
                type="button"
                onClick={() => void app.signOut()}
                style={sx(
                  'background:none;border:0;color:#8fb3ac;font-size:11px;cursor:pointer;padding:0;text-decoration:underline',
                )}
              >
                Sign out
              </button>
            </div>
          </div>
        </header>

        <nav
          data-chrome="1"
          style={sx(
            'background:linear-gradient(180deg,#14564d,#10453e);padding:0 8px;display:flex;gap:2px;flex-wrap:wrap;overflow-x:auto;box-shadow:0 6px 14px -10px rgba(0,0,0,.6) inset,0 4px 10px -8px rgba(0,0,0,.5)',
          )}
        >
          {navItems.map(([key, label, badge]) => (
            <button
              key={key}
              type="button"
              className="pms-nav-tab"
              aria-current={view === key ? 'page' : undefined}
              onClick={() => setView(key)}
              style={sxc(
                'border:0;padding:11px 14px;font-size:13px;font-weight:500;cursor:pointer;white-space:nowrap;border-radius:6px 6px 0 0',
                `background:${view === key ? '#f7fbfa' : 'transparent'}`,
                `color:${view === key ? '#0d3b35' : '#a9cbc4'}`,
                `border-bottom:3px solid ${view === key ? '#c9a227' : 'transparent'}`,
              )}
            >
              {label}
              {badge ? <span style={sx('color:#c9a227;font-weight:700')}> {badge}</span> : null}
            </button>
          ))}
        </nav>

        <main style={sx('flex:1')}>
          {me.mustChangePassword ? (
            <PasswordBanner onGo={() => setView(exec ? 'account' : 'data')} />
          ) : null}
          {view === 'overview' && <Overview />}
          {view === 'dashboard' && <Dashboard />}
          {view === 'entry' && (
            <Entry focusWeek={focusWeek} onFocusHandled={() => setFocusWeek(null)} />
          )}
          {view === 'approvals' && <Approvals onOpen={openWeek} />}
          {view === 'payments' && <Payments />}
          {view === 'issues' && <Issues />}
          {view === 'packages' && <Packages />}
          {view === 'reports' && <Reports />}
          {view === 'data' && <DataMethod />}
          {view === 'account' && <Account />}
        </main>
      </div>
      <SaveIndicator saving={app.saving} />
      <Toast />
    </div>
  );
}

function PasswordBanner({ onGo }: { onGo: () => void }) {
  return (
    <div
      data-chrome="1"
      style={sx(
        'background:#fdf3e3;border-bottom:1px solid #f2e0bd;padding:11px 18px;display:flex;gap:12px;align-items:center;flex-wrap:wrap;font-size:13px;color:#7a5410',
      )}
    >
      <span>
        You are still using the password issued to you. Change it now so the account is yours alone.
      </span>
      <button
        type="button"
        onClick={onGo}
        style={sx(
          'background:#b06a12;color:#fff;border:0;padding:7px 13px;border-radius:4px;font-size:12.5px;font-weight:600;cursor:pointer',
        )}
      >
        Change password
      </button>
    </div>
  );
}

function SaveIndicator({ saving }: { saving: boolean }) {
  if (!saving) return null;
  return (
    <div
      data-chrome="1"
      aria-live="polite"
      style={sx(
        'position:fixed;left:16px;bottom:16px;background:rgba(13,59,53,.92);color:#e8f0ee;padding:8px 14px;border-radius:99px;font-size:12px;z-index:60;box-shadow:0 12px 24px -14px rgba(0,0,0,.8)',
      )}
    >
      Saving…
    </div>
  );
}

function Toast() {
  const { toast } = useApp();
  if (!toast) return null;
  const bad = toast.tone === 'bad';
  return (
    <div
      data-chrome="1"
      role="status"
      style={sxc(
        'position:fixed;right:16px;bottom:16px;max-width:min(420px,calc(100vw - 32px));padding:12px 16px;border-radius:8px;font-size:13px;line-height:1.5;z-index:60;box-shadow:0 18px 32px -18px rgba(0,0,0,.7)',
        bad ? 'background:#fbeae7;color:#a3372c;border:1px solid #f0cfc9' : 'background:#eaf5ee;color:#2f7d51;border:1px solid #cbe6d5',
      )}
    >
      {toast.text}
    </div>
  );
}
